import { useState, useRef, useEffect, useCallback, type FC, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface MetricTooltipProps {
  title: string;
  explanation?: string;
  placement?: "top" | "bottom";
  children: ReactNode;
}

/**
 * MetricTooltip renders an explanation modal styled identically to the ApexCharts
 * hover modal from the revenue line chart (src/index.css: .apexcharts-tooltip & .apexcharts-tooltip-title).
 *
 * Uses createPortal to document.body to ensure:
 * 1. It is never clipped by table containers (e.g. overflow-x-auto, rounded card borders)
 *    even on tables with only 1-2 rows.
 * 2. It does not inherit table header styles like text-transform: uppercase.
 */
export const MetricTooltip: FC<MetricTooltipProps> = ({
  title,
  explanation,
  placement = "top",
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    position: "top" | "bottom";
  } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback((): {
    top: number;
    left: number;
    position: "top" | "bottom";
  } | null => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 280;
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 90;
    const margin = 8;

    // Check if there is enough space above the trigger in the current viewport
    const fitsTop = rect.top >= tooltipHeight + margin;
    const actualPlacement: "top" | "bottom" =
      placement === "top" ? (fitsTop ? "top" : "bottom") : "bottom";

    // Center horizontally over the trigger, bounded by viewport edges
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    if (left < 12) left = 12;
    if (left + tooltipWidth > window.innerWidth - 12) {
      left = window.innerWidth - tooltipWidth - 12;
    }

    let top = 0;
    if (actualPlacement === "top") {
      top = rect.top + window.scrollY - tooltipHeight - margin;
    } else {
      top = rect.bottom + window.scrollY + margin;
    }

    return { top, left, position: actualPlacement };
  }, [placement]);

  const handleMouseEnter = () => {
    const pos = calculatePosition();
    if (pos) setCoords(pos);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    // Refine vertical position once the tooltip element is measured in the DOM
    const pos = calculatePosition();
    if (pos) {
      setCoords(pos);
    }

    const handleScrollOrResize = () => {
      setIsOpen(false);
    };

    window.addEventListener("scroll", handleScrollOrResize, { passive: true });
    window.addEventListener("resize", handleScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, calculatePosition]);

  if (!explanation) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-flex items-center cursor-help border-b border-dotted border-gray-400/60 dark:border-gray-500/60"
      >
        {children}
      </span>
      {isOpen &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: "absolute",
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              width: "280px",
              zIndex: 9999,
            }}
            className="pointer-events-none normal-case rounded-lg border border-gray-200/80 bg-white text-left font-sans shadow-xl dark:border-gray-600 dark:bg-gray-700 transition-opacity duration-150"
          >
            {/* Header bar matching .apexcharts-tooltip-title */}
            <div className="rounded-t-lg border-b border-gray-200 bg-gray-100 px-3.5 py-2 text-xs font-semibold normal-case text-gray-900 dark:border-gray-500 dark:bg-gray-600 dark:text-white">
              {title}
            </div>
            {/* Body text matching .apexcharts-tooltip body */}
            <div className="px-3.5 py-2.5 text-xs font-normal normal-case leading-relaxed text-gray-600 dark:text-gray-300">
              {explanation}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
